import { Spacing } from '@persona-id/design-tokens';
import { CheckIcon, LockIcon } from '@persona-id/design-tokens/icons';
import { lowerCase, startCase } from 'lodash/fp';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import { SectionDescription } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import ContextMenu from 'dashboard/components/ContextMenu';
import { MatchRequirement } from 'dashboard/components/ExtractionPanel/util';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import useLearnMoreEventClick from 'dashboard/components/ProductUpsellTooltip/useLearnMoreEventClick';
import SearchInput from 'dashboard/components-lib/SearchInput';
import {
  DefinitionType,
  ExtractionRequest,
  ExtractionType,
  FieldWriteBackRequest,
  FieldWriteBackSourceType,
  GuidedPreset,
  MatchLevel,
} from 'dashboard/models/document';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';

const TableContainer = styled.div`
  max-height: 512px;
  overflow-y: auto;
`;

const EmptyTable = ({ colSpan, children }: { colSpan: number; children: React.ReactNode }) => (
  <TableRow style={{ height: '80px' }}>
    <TableCell colSpan={colSpan}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    </TableCell>
  </TableRow>
);

const LearnMoreTable = ({
  id,
  accessTier,
  colSpan,
}: {
  id: string;
  accessTier: Exclude<EditorAccessTier, EditorAccessTier.Any>;
  colSpan: number;
}) => {
  const { linkElementId, onLearnMoreClick } = useLearnMoreEventClick({ id });

  return (
    <EmptyTable colSpan={colSpan}>
      <Label weight='bold'>This feature is not available on your plan</Label>
      <Spacer height={0.5} />
      <a
        href='#'
        target='_blank'
        id={linkElementId}
        rel='noreferrer'
        onClick={(e) => {
          e.preventDefault();
          onLearnMoreClick({ accessTier });
        }}
        style={{ textDecoration: 'underline' }}
      >
        Learn more
      </a>
    </EmptyTable>
  );
};

const ActivableTableRow = styled(TableRow)`
  background-color: ${({ theme, active }) => active && theme.color.background.highlight};
`;

const StyledPre = styled.pre`
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
`;

interface ExtractionsTableProps {
  extractions: ExtractionRequest[];
  requiredExtractionsFieldNames: string[];
  selected: number | null;
  onSelect: (index?: number | null) => void;
  onDelete: (index: number) => void;
  fieldWriteBacks: FieldWriteBackRequest[];
  editable: boolean;
  hasInquiryTemplate: boolean;
}

const ExtractionsTableWithMultipleFieldWritebacks = ({
  extractions,
  requiredExtractionsFieldNames,
  selected,
  onSelect,
  onDelete,
  fieldWriteBacks,
  editable,
  hasInquiryTemplate,
}: ExtractionsTableProps) => {
  const hasAccessForEnhancedTier = useHasAccessForTierDeterminator()(
    EditorAccessTier.InquiriesEnhanced,
  );

  const [searchString, setSearchString] = useState('');

  const filteredExtractions = extractions
    .map((extraction, index) => [extraction, index] as const)
    .filter(([e]) => lowerCase(e['field-name']).includes(lowerCase(searchString)));

  useEffect(() => {
    setSearchString('');
  }, [extractions]);

  const handleDelete = (index: number) => {
    onDelete(index);
    if (selected === index) {
      onSelect(undefined);
    } else if (selected != null && selected > index) {
      onSelect(selected - 1);
    }
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={
            <Button
              id='extractions-card__add-button'
              variant='basic'
              size='sm'
              onClick={() => onSelect(null)}
              disabled={!editable || !hasAccessForEnhancedTier}
            >
              Add extraction
            </Button>
          }
        >
          Extractions
          {!hasAccessForEnhancedTier && (
            <ProductUpsellTooltip
              accessTier={EditorAccessTier.InquiriesEnhanced}
              learnMoreId='verifications_document_extractions'
              trigger={
                <div>
                  <InquiriesGatingSticker stickerType={EnhancedGatingStickerType.Limited} />
                </div>
              }
            />
          )}
        </Card.Title>
        <SectionDescription>
          Extractions identify and gather textual information from a document. This information may
          be compared against a source value or identified automatically. The result of this check
          will be displayed under Extracted properties detection in the dashboard.
        </SectionDescription>
        <SearchInput
          title={null}
          placeholder='Search extractions'
          value={searchString}
          handleInput={setSearchString}
          disabled={!editable || !hasAccessForEnhancedTier}
        />
        <Spacer height={0.5} />
        <TableContainer>
          <Table>
            <TableHead sticky>
              <TableRow>
                <TableHeaderCell>Label & key</TableHeaderCell>
                <TableHeaderCell>Compare against</TableHeaderCell>
                <TableHeaderCell>Match requirement</TableHeaderCell>
                <TableHeaderCell>Required</TableHeaderCell>
                <TableHeaderCell>Save to document field</TableHeaderCell>
                <TableHeaderCell width='min' />
              </TableRow>
            </TableHead>
            <TableBody>
              {!hasAccessForEnhancedTier && extractions.length === 0 ? (
                <LearnMoreTable
                  id='verifications_document_extractions'
                  accessTier={EditorAccessTier.InquiriesEnhanced}
                  colSpan={6}
                />
              ) : extractions.length === 0 ? (
                <EmptyTable colSpan={6}>No extractions configured</EmptyTable>
              ) : filteredExtractions.length === 0 ? (
                <EmptyTable colSpan={6}>No extractions found</EmptyTable>
              ) : (
                filteredExtractions.map(([extraction, index]) => {
                  const isGuided = extraction['extraction-type'] === ExtractionType.Guided;
                  const unconfigurable =
                    (extraction.version === '3.0' &&
                      extraction['definition-type'] === DefinitionType.Custom) ||
                    (!hasInquiryTemplate &&
                      extraction.version === '3.0' &&
                      extraction.preset &&
                      ![
                        GuidedPreset.GenericLooseAi,
                        GuidedPreset.GenericLoose,
                        GuidedPreset.GenericStrict,
                      ].includes(extraction.preset));

                  const legacy =
                    extraction['extraction-type'] === ExtractionType.Guided &&
                    extraction.version === '2.0';

                  const extractionFieldWritebacks = fieldWriteBacks.filter((fieldWriteBack) =>
                    fieldWriteBack.options.some(
                      (option) =>
                        option['source-type'] === FieldWriteBackSourceType.Extraction &&
                        option['source-key'] === extraction['field-name'],
                    ),
                  );
                  let presetType: 'address' | 'name' | 'custom';
                  if (extraction.version === '2.0') {
                    presetType = 'custom';
                  } else {
                    switch (extraction.preset) {
                      case GuidedPreset.AddressLoose:
                      case GuidedPreset.AddressStrict:
                        presetType = 'address';
                        break;
                      case GuidedPreset.NameLoose:
                      case GuidedPreset.NameStrict:
                        presetType = 'name';
                        break;
                      default:
                        presetType = 'custom';
                    }
                  }
                  const compareAgainstFields =
                    (extraction['extraction-type'] === ExtractionType.Guided &&
                      (extraction.version === '2.0'
                        ? [extraction['field-name']]
                        : extraction.subfields.map((s) => s['source-field']))) ||
                    [];
                  let matchRequirement;
                  if (isGuided) {
                    // for extraction 2.0 we map match level minimum to match requirement
                    // partial match level minimum is a loose match requirement
                    // full match level minimum is a strict match requirement
                    if (extraction.version === '2.0') {
                      switch (extraction['match-level-minimum']) {
                        case MatchLevel.Partial:
                          matchRequirement = MatchRequirement.Loose;
                          break;
                        case MatchLevel.Full:
                          matchRequirement = MatchRequirement.Strict;
                          break;
                      }
                    } else {
                      // for extractions 3.0 match level minimum are not used
                      // for custom extractions, we use the match levels defined in match criteria
                      // as a proxy for match requirement
                      // if a partial match is defined in the match criteria it is a loose match requirement
                      // if a full match is defined in the match criteria it is a strict match requirement
                      if (extraction['definition-type'] === DefinitionType.Custom) {
                        const matchLevelsDefined = extraction['match-criteria'].map(
                          (mc) => mc['match-level'],
                        );
                        if (matchLevelsDefined.includes(MatchLevel.Partial)) {
                          matchRequirement = MatchRequirement.Loose;
                        } else if (matchLevelsDefined.includes(MatchLevel.Full)) {
                          matchRequirement = MatchRequirement.Strict;
                        }
                      } else {
                        // for preset extractions, we use the match levels defined in match criteria
                        // we use the preset definitions to determine the match requirement
                        switch (extraction.preset) {
                          case GuidedPreset.AddressLoose:
                          case GuidedPreset.NameLoose:
                          case GuidedPreset.GenericLoose:
                          case GuidedPreset.GenericLooseAi:
                            matchRequirement = MatchRequirement.Loose;
                            break;
                          case GuidedPreset.AddressStrict:
                          case GuidedPreset.NameStrict:
                          case GuidedPreset.GenericStrict:
                            matchRequirement = MatchRequirement.Strict;
                            break;
                        }
                      }
                    }
                  }

                  return (
                    <ActivableTableRow
                      key={index}
                      id={extraction['field-name']}
                      onClick={
                        editable && hasAccessForEnhancedTier && !unconfigurable
                          ? () => onSelect(index)
                          : undefined
                      }
                      active={index === selected}
                    >
                      <TableCell>
                        {startCase(extraction['field-name'])}
                        {unconfigurable && (
                          <Tooltip
                            iconTrigger={<LockIcon size={12} style={{ marginLeft: Spacing[1] }} />}
                          >
                            This extraction cannot be configured in the dashboard. Please contact
                            customer support for assistance.
                          </Tooltip>
                        )}
                        <Paragraph color='tertiary'>{extraction['field-name']}</Paragraph>
                      </TableCell>
                      <TableCell>
                        {compareAgainstFields.length !== 0 ? (
                          compareAgainstFields.length === 1 ? (
                            <StyledPre>{compareAgainstFields[0]}</StyledPre>
                          ) : (
                            <Tooltip
                              side='right'
                              iconTrigger={<Sticker>{startCase(presetType)} </Sticker>}
                              contentAlignment='start'
                            >
                              {compareAgainstFields.map((f, index) => (
                                <div
                                  key={`${extraction['field-name']}-${f}-${index}`}
                                >{`\u2022 ${f}`}</div>
                              ))}
                            </Tooltip>
                          )
                        ) : (
                          <Paragraph weight='light'>--</Paragraph>
                        )}
                      </TableCell>
                      <TableCell>
                        {matchRequirement ? (
                          startCase(matchRequirement) + (legacy ? ' (Legacy)' : '')
                        ) : (
                          <Paragraph weight='light'>N/A</Paragraph>
                        )}
                      </TableCell>
                      <TableCell>
                        {requiredExtractionsFieldNames.includes(extraction['field-name']) ? (
                          <CheckIcon />
                        ) : (
                          <Paragraph weight='light'>--</Paragraph>
                        )}
                      </TableCell>
                      <TableCell>
                        {extractionFieldWritebacks.length === 0 ? (
                          <Paragraph weight='light'>None</Paragraph>
                        ) : (
                          <span style={{ display: 'flex', columnGap: Spacing[1] }}>
                            <span>{extractionFieldWritebacks[0]['field-name']}</span>
                            {extractionFieldWritebacks.length > 1 && (
                              <Tooltip
                                side='bottom'
                                trigger={
                                  <Paragraph
                                    style={{ lineHeight: '14px' }}
                                  >{` + ${extractionFieldWritebacks.length - 1} more`}</Paragraph>
                                }
                              >
                                {extractionFieldWritebacks
                                  .slice(1)
                                  .map((fw) => fw['field-name'])
                                  .join(', ')}
                              </Tooltip>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ContextMenu
                          variant='inverse'
                          disabled={!editable || !hasAccessForEnhancedTier}
                          items={[
                            {
                              id: 'extractions-table__context-menu-delete',
                              label: 'Delete',
                              onClick: () => handleDelete(index),
                              variant: 'danger',
                            },
                          ]}
                          renderItem={(item) => (
                            <ContextMenu.Item {...item}>{item.label}</ContextMenu.Item>
                          )}
                        />
                      </TableCell>
                    </ActivableTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card.Body>
    </Card>
  );
};

export default ExtractionsTableWithMultipleFieldWritebacks;
